import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Minus, PackageSearch, Plus, RefreshCw, Search, WifiOff } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { useAvisos } from '../lib/contextoAvisos';
import { useEscanerTeclado } from '../lib/useEscanerTeclado';
import EscanerCamara from '../mobile/EscanerCamara';
import Ayuda from '../ui/Ayuda';
import {
  capturar, contarPendientesCaptura, descartarRechazada, llaveCola, pendientesCaptura, recordarUbicaciones,
  reenviar, rechazadasCaptura, ubicacionesRecordadas,
} from './colaCaptura';
import { CONFIANZA, ESTADO, queSeHaria } from './migracion';
import {
  avisoCola, avisoGuardada, cantidadValida, motivoCaptura, normalizarCantidad, textoPendientes, textoSerie,
} from './captura';

/* =====================================================================
   «Captura rápida» · U-04 fase a2 (DISEÑO-U-04 §6.3)
   =====================================================================
   Escanear (lector o cámara) o teclear el código, poner cuántas hay y
   seguir con el siguiente. Cada captura es una fila de preparación del
   servidor: nada entra al inventario hasta que se revisa y se guarda.

   «GUARDAR Y SIGUIENTE» VA FIJO ABAJO Y NO TAPA LA CANTIDAD (prueba 46).
   Es `sticky` y no `fixed`: ocupa su sitio al final del formulario, y ese
   sitio es el relleno que deja la cantidad por encima. Se pega a 80 px del
   borde para librar los dos flotantes de la casa («Pedir ayuda» y
   «Preguntar», 64 px), y la caja de la cantidad reserva por debajo
   (`scroll-mb-48`) lo que mide la barra pegada: al enfocarla, el navegador
   la deja a la vista. Dentro de la barra solo va el botón y su motivo; con
   campos dentro cubriría más del 15 % de la pantalla (M6).

   EL CÓDIGO SE COMPRUEBA CON LO QUE YA EXISTE: `GET /api/qr/resolver`
   (¿es de un producto?) y, si no, `GET /api/gtin/sugerencia` (¿cómo se
   llama, según la copia del catálogo?). Ninguna de las dos se cambia
   (D12). Sin señal no se comprueba: se captura igual y el servidor lo
   resuelve al llegar.
   ===================================================================== */

const BOTON = 'min-h-11 rounded-xl px-3 text-sm font-semibold disabled:opacity-50';
const SECUNDARIO = `${BOTON} border border-gray-300 bg-white text-gray-800`;
const VACIO = { estado: 'esperando' };

function Capturada({ fila, enLinea, idMigracion, onCorregida }) {
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [cantidad, setCantidad] = useState(String(fila.interpretacion?.cantidad ?? ''));
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const aplicada = fila.estado === 'APLICADA';
  const c = CONFIANZA[fila.confianza] || CONFIANZA.BAJA;
  const malo = !cantidadValida(cantidad);
  const motivo = aplicada ? 'Ya está guardada en el inventario: una diferencia se corrige contando.'
    : !enLinea ? 'Sin conexión: espera a que vuelva la señal.' : '';

  async function guardar(e) {
    e.preventDefault();
    if (malo) return;
    setOcupado(true);
    setError('');
    try {
      await api.patch(`/migracion/${idMigracion}/filas/${fila.id}`, { cantidad: normalizarCantidad(cantidad) });
      setCorrigiendo(false);
      onCorregida(fila);
    } catch (err) {
      setError(err.message);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <li className="rounded-xl border border-gray-200 bg-white p-3" data-capturada={fila.id}>
      <p className="font-medium text-gray-900 break-words">{queSeHaria(fila)}</p>
      <p className="mt-1 text-xs text-gray-600 break-words">
        <span className={`mr-1 inline-flex rounded-full border px-2 py-0.5 font-semibold ${c.clase}`}>Confianza {c.palabra}</span>
        {ESTADO[fila.estado] || fila.estado}{fila.frase ? ` · ${fila.frase}` : ''}
      </p>
      {!corrigiendo && (
        <>
          <button type="button" className={`${SECUNDARIO} mt-2 w-full`} disabled={Boolean(motivo)}
                  onClick={() => setCorrigiendo(true)}>
            Corregir
          </button>
          {motivo && <p className="mt-1 text-xs text-gray-600">{motivo}</p>}
        </>
      )}
      {corrigiendo && (
        <form className="mt-2 flex flex-col gap-2" onSubmit={guardar}>
          <label className="text-sm text-gray-800">Cantidad
            <input className="entrada" inputMode="decimal" data-campo="corregir-cantidad" value={cantidad}
                   onChange={(e) => setCantidad(e.target.value.replace(/\s+/g, ''))} />
          </label>
          {malo && <p className="text-xs text-red-700">{motivoCaptura({ cantidad, producto: { estado: 'existe' } })}</p>}
          {error && <p className="text-xs text-red-700 break-words">{error}</p>}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className={SECUNDARIO} onClick={() => setCorrigiendo(false)}>Cancelar</button>
            <button type="submit" className={`${BOTON} bg-[var(--color-marca)] text-white`} disabled={malo || ocupado}>
              Guardar corrección
            </button>
          </div>
        </form>
      )}
    </li>
  );
}

export default function CapturaEnSerie({ migracion, Camara = EscanerCamara }) {
  const { usuario } = useAuth();
  const avisos = useAvisos();
  const idMigracion = migracion?.id;
  const llave = llaveCola(usuario, idMigracion);
  const omision = migracion?.ubicacion_omision?.codigo || '';

  const [codigo, setCodigo] = useState('');
  const [medio, setMedio] = useState(null);
  const [producto, setProducto] = useState(VACIO);
  const [nombre, setNombre] = useState('');
  const [cantidad, setCantidad] = useState('1');
  const [ubicacion, setUbicacion] = useState(omision);
  const [ubicaciones, setUbicaciones] = useState(() => ubicacionesRecordadas(usuario));
  const [camara, setCamara] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [enLinea, setEnLinea] = useState(typeof navigator === 'undefined' || navigator.onLine !== false);
  const [pendientes, setPendientes] = useState(() => pendientesCaptura(llave));
  const [rechazadas, setRechazadas] = useState(() => rechazadasCaptura(llave));
  const [mias, setMias] = useState({ filas: [], total: null, cargando: true, error: '' });
  const campoCodigo = useRef(null);
  const consulta = useRef(0);

  const refrescarCola = useCallback(() => {
    setPendientes(pendientesCaptura(llave));
    setRechazadas(rechazadasCaptura(llave));
  }, [llave]);

  const cargarMias = useCallback(async () => {
    if (!idMigracion) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setMias((m) => ({ ...m, cargando: false }));
      return;
    }
    try {
      const r = await api.getConTotal(`/migracion/${idMigracion}/captura/mias?limite=5`);
      setMias({ filas: Array.isArray(r.filas) ? r.filas : [], total: r.total, cargando: false, error: '' });
    } catch (e) {
      setMias((m) => ({ ...m, cargando: false, error: e.message }));
    }
  }, [idMigracion]);

  const enviarPendientes = useCallback(async () => {
    if (!llave || contarPendientesCaptura(llave) === 0) return;
    const r = await reenviar(llave, idMigracion);
    refrescarCola();
    if (r.enviados) {
      avisos.exito(r.enviados === 1 ? 'Se envió 1 captura que esperaba en este teléfono.'
        : `Se enviaron ${r.enviados} capturas que esperaban en este teléfono.`);
      await cargarMias();
    }
    if (r.rechazadas.length) avisos.aviso(`${r.rechazadas[0].motivo} Revísala en «Lo último que capturaste».`);
  }, [llave, idMigracion, refrescarCola, cargarMias, avisos]);

  useEffect(() => {
    let vigente = true;
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
      api.get('/ubicaciones').then((u) => {
        if (!vigente || !Array.isArray(u)) return;
        setUbicaciones(u);
        recordarUbicaciones(usuario, u);
      }).catch(() => {});
    }
    return () => { vigente = false; };
  }, [usuario]);

  useEffect(() => { cargarMias(); enviarPendientes(); }, [cargarMias, enviarPendientes]);

  useEffect(() => {
    const alVolver = () => { setEnLinea(true); enviarPendientes(); };
    const alIrse = () => setEnLinea(false);
    window.addEventListener('online', alVolver);
    window.addEventListener('offline', alIrse);
    // Un servidor que no contestó (5xx) no dispara «online»: mientras haya
    // algo esperando, se reintenta solo cada 20 s.
    const iv = setInterval(() => {
      if (navigator.onLine !== false && contarPendientesCaptura(llave) > 0) enviarPendientes();
    }, 20000);
    return () => {
      window.removeEventListener('online', alVolver);
      window.removeEventListener('offline', alIrse);
      clearInterval(iv);
    };
  }, [enviarPendientes, llave]);

  const comprobar = useCallback(async (texto, comoLlego) => {
    const c = String(texto || '').replace(/\s+/g, '');
    if (!c) return;
    const yo = consulta.current + 1;
    consulta.current = yo;
    setCodigo(c);
    setMedio(comoLlego);
    setError('');
    setNombre('');
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setProducto({ estado: 'desconocido', sinRed: true });
      return;
    }
    setProducto({ estado: 'buscando' });
    try {
      const r = await api.get(`/qr/resolver?codigo=${encodeURIComponent(c)}`);
      if (consulta.current !== yo) return;
      if (r?.tipo === 'PRODUCTO') setProducto({ estado: 'existe', datos: r.datos || {} });
      else if (r?.tipo === 'PIEZA') setProducto({ estado: 'pieza' });
      else setProducto({ estado: 'otro' });
    } catch (e) {
      if (consulta.current !== yo) return;
      if (!(e instanceof ErrorApi) || e.status !== 404) {
        setProducto({ estado: 'desconocido', motivo: e.message });
        return;
      }
      // No es de ningún producto de la empresa: ¿cómo se llama, según el catálogo?
      let sugerido = '';
      try {
        const s = await api.get(`/gtin/sugerencia?codigo=${encodeURIComponent(c)}`);
        sugerido = s?.sugerencia?.nombre_propuesto || s?.sugerencia?.nombre || '';
      } catch { /* sin sugerencia: se escribe a mano */ }
      if (consulta.current !== yo) return;
      setNombre(sugerido);
      setProducto({ estado: 'nuevo', sugerido: Boolean(sugerido) });
    }
  }, []);

  const leidoConLaCamara = useCallback((t) => { setCamara(false); comprobar(t, 'ESCANEADO'); }, [comprobar]);
  const cerrarCamara = useCallback(() => setCamara(false), []);

  // El lector USB o Bluetooth teclea en la página entera y manda Enter:
  // llega aquí aunque el foco esté en la cantidad, y eso es lo que se
  // quiere al ir de un producto al siguiente.
  useEscanerTeclado((leido) => comprobar(leido, 'ESCANEADO'), { activo: !camara && !guardando });

  function limpiar() {
    consulta.current += 1;
    setCodigo('');
    setMedio(null);
    setProducto(VACIO);
    setNombre('');
    setCantidad('1');
    setError('');
    campoCodigo.current?.focus?.();
  }

  const motivo = guardando ? '' : motivoCaptura({ codigo, producto, nombre, cantidad });

  async function guardar(e) {
    e.preventDefault();
    if (motivo || guardando || !llave) return;
    const datos = { cantidad: normalizarCantidad(cantidad), ubicacion: ubicacion || omision || undefined };
    if (producto.estado !== 'sin_codigo' && codigo) {
      datos.codigo = codigo;
      datos.origen = medio === 'ESCANEADO' ? 'ESCANEADO' : 'TECLEADO';
    }
    if (producto.estado !== 'existe' && nombre.trim()) datos.nombre = nombre.trim();
    if (!datos.ubicacion) delete datos.ubicacion;
    const dicho = producto.estado === 'existe' ? (producto.datos?.nombre || codigo) : (datos.nombre || codigo);
    setGuardando(true);
    setError('');
    try {
      const r = await capturar(llave, idMigracion, datos);
      if (r.estado === 'enviado') {
        avisos.exito(avisoGuardada(datos.cantidad, dicho), { duracion: 2000 });
        globalThis.navigator?.vibrate?.(40);
      } else {
        avisos.info(avisoCola(), { duracion: 2000 });
      }
      refrescarCola();
      limpiar();
      if (r.estado === 'enviado') await cargarMias();
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  }

  // Con la función de estado: dos toques seguidos suman dos, no uno.
  const masMenos = (d) => setCantidad((antes) => {
    const n = Number(normalizarCantidad(antes)) || 0;
    return String(Math.max(0, Math.round((n + d) * 1000) / 1000));
  });

  const nPendientes = pendientes.length;
  const ubicacionNombre = migracion?.ubicacion_omision?.nombre || omision;
  const conCodigo = producto.estado !== 'sin_codigo';

  return (
    <div className="flex flex-col gap-4" data-migracion="captura">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Captura rápida</h1>
        <p className="text-sm text-gray-600">Escanea, escribe cuántas hay y sigue con el siguiente.</p>
      </div>
      <Ayuda id="migracion.captura.pantalla" />

      {!enLinea && (
        <div className="rounded-xl border border-yellow-300 bg-yellow-100 p-3 text-sm text-yellow-900"
             data-banda="sin-red" {...(nPendientes ? { 'data-pendientes': nPendientes } : {})}>
          <p className="flex items-start gap-2 font-semibold">
            <WifiOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{textoPendientes(nPendientes)}</span>
          </p>
          <Ayuda id="migracion.captura.cola" />
        </div>
      )}
      {enLinea && nPendientes > 0 && (
        <div className="rounded-xl border border-yellow-300 bg-yellow-100 p-3 text-sm text-yellow-900"
             data-banda="pendientes" data-pendientes={nPendientes}>
          <p className="font-semibold">
            {nPendientes === 1 ? '1 captura espera en este teléfono para enviarse.'
              : `${nPendientes} capturas esperan en este teléfono para enviarse.`}
          </p>
          <button type="button" className={`${SECUNDARIO} mt-2 w-full`} onClick={enviarPendientes}>
            <RefreshCw className="mr-1 inline h-4 w-4" aria-hidden="true" />Enviar ahora
          </button>
        </div>
      )}

      {/* EL CÓDIGO. Formulario propio: Enter aquí es «comprobar este código»
          (tecleado). El lector manda su Enter por `useEscanerTeclado`, que
          lo detiene antes de que llegue a este formulario. */}
      <form className="flex flex-col gap-2" data-form="codigo"
            onSubmit={(e) => { e.preventDefault(); comprobar(codigo, 'TECLEADO'); }}>
        <label className="flex flex-col gap-1 text-sm font-semibold text-gray-800">
          Código del producto
          <input ref={campoCodigo} className="entrada font-mono" data-campo="codigo" value={codigo} autoFocus
                 autoComplete="off" autoCapitalize="characters" spellCheck={false} enterKeyHint="search"
                 disabled={!conCodigo}
                 onChange={(e) => {
                   consulta.current += 1;
                   setCodigo(e.target.value.replace(/\s+/g, ''));
                   setMedio('TECLEADO');
                   if (producto.estado !== 'esperando') { setProducto(VACIO); setNombre(''); }
                 }} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button type="submit" className={SECUNDARIO} disabled={!codigo || !conCodigo}>
            <Search className="mr-1 inline h-4 w-4" aria-hidden="true" />Buscar
          </button>
          {Camara && (
            <button type="button" className={SECUNDARIO} disabled={!conCodigo} onClick={() => setCamara((v) => !v)}>
              <Camera className="mr-1 inline h-4 w-4" aria-hidden="true" />{camara ? 'Cerrar cámara' : 'Leer con la cámara'}
            </button>
          )}
        </div>
        {camara && <Camara onCodigo={leidoConLaCamara} onCerrar={cerrarCamara} />}
        <button type="button" className={`${SECUNDARIO} w-full`}
                onClick={() => {
                  if (conCodigo) { limpiar(); setProducto({ estado: 'sin_codigo' }); setCamara(false); } else limpiar();
                }}>
          {conCodigo ? 'No tiene código' : 'Sí tiene código: escanearlo'}
        </button>
        {conCodigo && <Ayuda id="migracion.captura.sin_codigo" />}
      </form>

      {/* LO DEMÁS: el producto, cuántas hay y dónde. La barra fija es el
          ÚLTIMO hijo de este formulario (prueba 46). */}
      <form className="flex flex-col gap-3" data-form="captura" onSubmit={guardar}>
        <div aria-live="polite">
          {producto.estado === 'esperando' && (
            <p className="flex items-start gap-2 rounded-xl bg-gray-50 p-3 text-sm text-gray-700">
              <PackageSearch className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Escanea o escribe el código del producto, o pulsa «No tiene código».</span>
            </p>
          )}
          {producto.estado === 'buscando' && (
            <p className="flex items-center gap-2 text-sm text-gray-600" aria-busy="true">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Comprobando el código {codigo}…
            </p>
          )}
          {producto.estado === 'existe' && (
            <div className="rounded-xl border border-green-300 bg-green-50 p-3" data-producto="existe">
              <p className="font-semibold text-gray-900 break-words">{producto.datos?.nombre || producto.datos?.sku}</p>
              <p className="text-xs text-gray-700 break-words">SKU {producto.datos?.sku} · ya está en tu catálogo: se suma lo que cuentes.</p>
            </div>
          )}
          {(producto.estado === 'pieza' || producto.estado === 'otro') && (
            <p className="rounded-xl bg-red-50 p-3 text-sm text-red-800 break-words" data-producto={producto.estado}>
              {motivoCaptura({ codigo, producto, nombre, cantidad })}
            </p>
          )}
        </div>

        {['nuevo', 'sin_codigo', 'desconocido'].includes(producto.estado) && (
          <label className="flex flex-col gap-1 text-sm font-semibold text-gray-800">
            {producto.estado === 'desconocido' ? 'Nombre (si es un producto nuevo)' : 'Cómo se llama el producto'}
            <input className="entrada" data-campo="nombre" value={nombre} maxLength={200}
                   onChange={(e) => setNombre(e.target.value)} />
            <span className="text-xs font-normal text-gray-600 break-words">
              {producto.estado === 'nuevo' && (producto.sugerido
                ? `El código ${codigo} no está en tu catálogo. El nombre viene del catálogo compartido: cámbialo si no es así.`
                : `El código ${codigo} no está en tu catálogo: se dará de alta con este nombre y este código.`)}
              {producto.estado === 'sin_codigo' && 'Se da de alta con este nombre.'}
              {producto.estado === 'desconocido' && (producto.sinRed
                ? `Sin conexión, TEGO comprueba el código ${codigo} al volver la señal. Si es un producto nuevo, escribe su nombre.`
                : `No se pudo comprobar el código ${codigo}: ${producto.motivo || ''} Si es un producto nuevo, escribe su nombre.`)}
            </span>
          </label>
        )}

        <div className="flex flex-col gap-1 scroll-mb-48" data-caja="cantidad">
          <label htmlFor="captura-cantidad" className="text-sm font-semibold text-gray-800">Cuántas hay</label>
          <div className="flex items-stretch gap-2">
            <button type="button" aria-label="Quitar uno" className={`${SECUNDARIO} min-w-14 text-lg`}
                    onClick={() => masMenos(-1)}>
              <Minus className="mx-auto h-5 w-5" aria-hidden="true" /><span className="sr-only">Quitar uno</span>
            </button>
            <input id="captura-cantidad" className="entrada min-w-0 flex-1 text-center text-lg" data-campo="cantidad"
                   inputMode="decimal" enterKeyHint="next" value={cantidad}
                   onChange={(e) => setCantidad(e.target.value.replace(/\s+/g, ''))} />
            <button type="button" aria-label="Sumar uno" className={`${SECUNDARIO} min-w-14 text-lg`}
                    onClick={() => masMenos(1)}>
              <Plus className="mx-auto h-5 w-5" aria-hidden="true" /><span className="sr-only">Sumar uno</span>
            </button>
          </div>
        </div>

        {ubicaciones.length > 1 ? (
          <label className="flex flex-col gap-1 text-sm font-semibold text-gray-800">
            Dónde está
            <select className="entrada" data-campo="ubicacion" value={ubicacion} onChange={(e) => setUbicacion(e.target.value)}>
              {!ubicaciones.some((u) => u.codigo === ubicacion) && <option value={ubicacion}>{ubicacionNombre || 'Elige una ubicación'}</option>}
              {ubicaciones.map((u) => <option key={u.id ?? u.codigo} value={u.codigo}>{u.nombre} ({u.codigo})</option>)}
            </select>
          </label>
        ) : (
          <p className="text-sm text-gray-700">Se guarda en {ubicacionNombre || 'la ubicación de esta migración'}.</p>
        )}

        {error && (
          <p className="rounded-xl bg-red-50 p-3 text-sm text-red-800 break-words" data-error="captura" role="alert">{error}</p>
        )}

        <div className="sticky bottom-20 z-30 flex flex-col gap-1 rounded-xl bg-white/95 p-1 shadow-md" data-barra-fija="">
          <button type="submit" className="min-h-12 w-full rounded-xl bg-[var(--color-marca)] px-3 font-semibold text-white disabled:opacity-50"
                  disabled={Boolean(motivo) || guardando}>
            {guardando ? 'Guardando…' : 'Guardar y siguiente'}
          </button>
          {motivo && <p className="px-1 text-xs text-gray-700" data-motivo="guardar">{motivo}</p>}
        </div>
      </form>

      <section className="flex flex-col gap-2" aria-labelledby="lo-ultimo">
        <h2 id="lo-ultimo" className="font-semibold text-gray-900">Lo último que capturaste</h2>
        {mias.total !== null && mias.total > 0 && <p className="text-sm text-gray-600">{textoSerie(mias.total)}</p>}
        {rechazadas.map((c) => (
          <div key={c.id_operacion} className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-900" data-rechazada={c.id_operacion}>
            <p className="font-semibold break-words">No entró: {c.cantidad} de «{c.nombre || c.codigo}»</p>
            <p className="break-words">{c.motivo}</p>
            <button type="button" className={`${SECUNDARIO} mt-2 w-full`}
                    onClick={() => { descartarRechazada(llave, c.id_operacion); refrescarCola(); }}>
              Quitar de la lista
            </button>
          </div>
        ))}
        {pendientes.length > 0 && (
          <ul className="flex flex-col gap-2">
            {pendientes.map((c) => (
              <li key={c.id_operacion} className="rounded-xl border border-yellow-300 bg-yellow-50 p-3 text-sm" data-en-telefono={c.id_operacion}>
                <p className="font-medium text-gray-900 break-words">{c.cantidad} de «{c.nombre || c.codigo}»</p>
                <p className="text-xs text-gray-700">En este teléfono, sin enviar.</p>
              </li>
            ))}
          </ul>
        )}
        {mias.cargando && (
          <p className="flex items-center gap-2 text-sm text-gray-600" aria-busy="true">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Leyendo lo último que capturaste…
          </p>
        )}
        {!mias.cargando && mias.error && (
          <div className="rounded-xl bg-red-50 p-3 text-sm text-red-800">
            <p className="break-words">No se pudo leer lo último que capturaste: {mias.error}</p>
            <button type="button" className={`${SECUNDARIO} mt-2 w-full`} onClick={cargarMias}>
              <RefreshCw className="mr-1 inline h-4 w-4" aria-hidden="true" />Volver a intentarlo
            </button>
          </div>
        )}
        {!mias.cargando && !mias.error && mias.filas.length === 0 && pendientes.length === 0 && rechazadas.length === 0 && (
          <p className="rounded-xl bg-gray-50 p-3 text-sm text-gray-600">Lo que captures aparece aquí.</p>
        )}
        {mias.filas.length > 0 && (
          <ul className="flex flex-col gap-2">
            {mias.filas.map((f) => (
              <Capturada key={f.id} fila={f} enLinea={enLinea} idMigracion={idMigracion}
                         onCorregida={() => { avisos.exito('Corregida. Vuelve a decidirla en la revisión.'); cargarMias(); }} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
